import React, { useEffect, useState } from "react";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import { api, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { Plus, ShieldCheck, PencilSimple, Trash, Sparkle, ArrowsCounterClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import useNewRecordHighlight from "@/hooks/useNewRecordHighlight";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import ExportMenu from "@/components/app/ExportMenu";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

const CATEGORIES = [
  { v: "registration", l: "Registration" },
  { v: "license", l: "License" },
  { v: "permit", l: "Permit" },
  { v: "tax_clearance", l: "Tax Clearance" },
  { v: "certification", l: "Certification" },
];

// Manually-settable statuses (admin states that override the auto-computed one).
const MANUAL_STATUSES = [
  { v: "active", l: "Active (auto)" },
  { v: "pending_review", l: "Pending review" },
  { v: "inactive", l: "Inactive" },
  { v: "archived", l: "Archived" },
];

// Warning threshold in days — anything inside this window is "expiring soon".
const EXPIRY_WARN_DAYS = 30;

const BLANK = {
  title: "", category: "registration", authority: "", reference_number: "",
  issue_date: "", expiry_date: "", status: "active", notes: "",
};

function dayDiff(dateStr) {
  if (!dateStr) return null;
  const ms = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Effective, rule-based status for a compliance record.
 *  - manual override states (pending_review, inactive, archived) win
 *  - otherwise the expiry date decides: past → expired, ≤30d → expiring_soon, else active
 */
function effectiveStatus(item) {
  const manual = item?.status;
  if (["pending_review", "inactive", "archived"].includes(manual)) return manual;
  if (!item?.expiry_date) return "active";
  const days = dayDiff(item.expiry_date);
  if (days == null) return "active";
  if (days < 0) return "expired";
  if (days <= EXPIRY_WARN_DAYS) return "expiring_soon";
  return "active";
}

function ComplianceForm({ initial, onSave, children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || BLANK); }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl" data-testid="compliance-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{initial?.id ? "Edit compliance item" : "New compliance item"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Title *</Label>
            <Input value={form.title} required onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="compliance-title-input" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="compliance-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Issuing authority</Label>
            <Input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} />
          </div>
          <div>
            <Label>Reference / Registration no.</Label>
            <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MANUAL_STATUSES.map((s) => (
                  <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[11px] text-slate-500">
              "Active" is computed automatically from the expiry date — you don't need to change it manually.
            </div>
          </div>
          <div>
            <Label>Issue date</Label>
            <Input type="date" value={form.issue_date || ""} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
          </div>
          <div>
            <Label>Expiry date</Label>
            <Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="compliance-expiry-input" />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy} data-testid="compliance-save-button">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RenewalHistoryDialog({ item, children }) {
  const [open, setOpen] = useState(false);
  const history = [...(item?.renewal_history || [])].reverse();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="renewal-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ClockCounterClockwise size={18} className="text-[#1E5CBA]" />
            Renewal history · <span className="truncate">{item.title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="flex items-center justify-between">
              <span className="overline text-emerald-700">Current version</span>
              <StatusBadge status={effectiveStatus(item)} />
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-y-1.5 text-xs text-slate-700">
              <dt className="font-medium text-slate-500">Reference #</dt><dd className="font-mono-alt">{item.reference_number || "—"}</dd>
              <dt className="font-medium text-slate-500">Authority</dt><dd>{item.authority || "—"}</dd>
              <dt className="font-medium text-slate-500">Issue date</dt><dd className="tabular-nums">{formatDate(item.issue_date) || "—"}</dd>
              <dt className="font-medium text-slate-500">Expiry date</dt><dd className="tabular-nums">{formatDate(item.expiry_date) || "—"}</dd>
              {item.last_renewed_at && (
                <>
                  <dt className="font-medium text-slate-500">Last renewed</dt>
                  <dd className="text-[11px]">{formatDateTime(item.last_renewed_at)} by {item.last_renewed_by || "—"}</dd>
                </>
              )}
            </dl>
          </div>
          <div className="divider-lite" />
          <div className="overline text-slate-500">Previous versions ({history.length})</div>
          {history.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
              No previous renewals recorded.
            </div>
          ) : (
            <ol className="space-y-2">
              {history.map((h, i) => (
                <li key={i} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`renewal-version-${h.version}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono-alt text-[11px] font-semibold text-slate-700">v{h.version}</span>
                    <span className="text-[11px] text-slate-500">
                      archived {formatDateTime(h.archived_at)} by {h.archived_by || "—"}
                    </span>
                  </div>
                  <dl className="mt-1.5 grid grid-cols-2 gap-y-1 text-xs text-slate-700">
                    <dt className="text-slate-500">Reference #</dt><dd className="font-mono-alt">{h.reference_number || "—"}</dd>
                    <dt className="text-slate-500">Authority</dt><dd>{h.authority || "—"}</dd>
                    <dt className="text-slate-500">Issue date</dt><dd className="tabular-nums">{formatDate(h.issue_date) || "—"}</dd>
                    <dt className="text-slate-500">Expiry date</dt><dd className="tabular-nums">{formatDate(h.expiry_date) || "—"}</dd>
                    {h.notes && <><dt className="text-slate-500">Notes</dt><dd>{h.notes}</dd></>}
                  </dl>
                </li>
              ))}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const canEdit = can(user, "compliance.manage") ?? hasMinRole(user, "manager");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { newId, highlightRef, classForId } = useNewRecordHighlight(loading);
  const inspector = useRecordInspector();

  const load = async () => {
    const { data } = await api.get("/compliance");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (form.id) {
      await api.put(`/compliance/${form.id}`, form);
      toast.success("Compliance item updated");
    } else {
      await api.post("/compliance", form);
      toast.success("Compliance item created");
    }
    load();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/compliance/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="compliance-page">
      <PageHeader
        eyebrow="Regulatory"
        title="Compliance Tracker"
        description="Monitor registrations, licenses, permits, and tax clearance certificates."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExportMenu module="compliance" />
            {canEdit && (
              <>
                <AiExtractDialog
                  module="compliance"
                  onSaved={load}
                  trigger={
                    <Button variant="outline" data-testid="compliance-ai-extract-btn">
                      <Sparkle size={14} className="mr-2" weight="fill" /> AI Extract
                    </Button>
                  }
                />
                <ComplianceForm onSave={save}>
                  <Button className="btn-primary-blue" data-testid="add-compliance-button">
                    <Plus size={14} className="mr-2" /> New item
                  </Button>
                </ComplianceForm>
              </>
            )}
          </div>
        }
      />
      <div className="p-4 md:p-8">
        {loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No compliance items yet"
            description="Start tracking licenses, permits, and tax certificates to get early expiry warnings — or use the AI Extractor to auto-draft one from a certificate scan."
          />
        ) : (
          <div className="dc-card responsive-table-wrap">
            <table className="data-table w-full min-w-[760px]">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Authority</th>
                  <th>Reference</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  {canEdit && <th className="w-28">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const days = dayDiff(c.expiry_date);
                  const eff = effectiveStatus(c);
                  const isNew = c.id === newId;
                  const renewedTimes = c.renewal_count || (c.renewal_history?.length || 0);
                  return (
                    <tr
                      key={c.id}
                      ref={isNew ? highlightRef : null}
                      className={`${classForId(c.id)} cursor-pointer hover:bg-slate-50/70 transition-colors`}
                      onClick={() => inspector.open(c, {
                        title: c.title || "Compliance item",
                        subtitle: `${c.category?.replace(/_/g, " ") || ""} · ${c.authority || ""}`,
                      })}
                      data-testid={`compliance-row-${c.id}`}
                    >
                      <td className="font-semibold text-zinc-950">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{c.title}</span>
                          {c.source === "ai_extraction" && (
                            <span className="dc-ai-chip" title="Imported via AI Extractor">
                              <Sparkle size={10} weight="fill" /> AI
                            </span>
                          )}
                          {renewedTimes > 0 && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono-alt text-[10px] text-slate-600"
                              title={`Renewed ${renewedTimes} time${renewedTimes === 1 ? "" : "s"}`}
                            >
                              <ClockCounterClockwise size={10} /> v{renewedTimes + 1}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="uppercase tracking-wider text-xs text-zinc-600">{c.category?.replace("_", " ")}</td>
                      <td>{c.authority || "—"}</td>
                      <td className="font-mono-alt text-xs">{c.reference_number || "—"}</td>
                      <td>
                        <div>
                          <div className="tabular-nums text-sm text-slate-800">{formatDate(c.expiry_date) || "—"}</div>
                          {days != null && (
                            <div className="text-[11px] text-slate-500">
                              {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d remaining`}
                            </div>
                          )}
                        </div>
                      </td>
                      <td><StatusBadge status={eff} /></td>
                      {canEdit && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            <AiExtractDialog
                              module="compliance"
                              mode="renew"
                              existingRecord={c}
                              onSaved={load}
                              trigger={
                                <Button size="sm" variant="outline" className="h-8" data-testid={`renew-compliance-${c.id}`} title="Renew / update this document">
                                  <ArrowsCounterClockwise size={12} className="mr-1" /> Renew
                                </Button>
                              }
                            />
                            {renewedTimes > 0 && (
                              <RenewalHistoryDialog item={c}>
                                <Button size="sm" variant="outline" className="h-8" data-testid={`history-compliance-${c.id}`} title="View renewal history">
                                  <ClockCounterClockwise size={12} />
                                </Button>
                              </RenewalHistoryDialog>
                            )}
                            <ComplianceForm initial={c} onSave={(f) => save({ ...f, id: c.id })}>
                              <Button size="sm" variant="outline" className="h-8" data-testid={`edit-compliance-${c.id}`} title="Edit">
                                <PencilSimple size={12} />
                              </Button>
                            </ComplianceForm>
                            <AuditTrailDrawer entity="compliance" entityId={c.id} label={c.title}
                              trigger={
                                <Button size="sm" variant="outline" className="h-8" data-testid={`audit-compliance-${c.id}`} title="Internal audit history">
                                  📜
                                </Button>
                              } />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="h-8" data-testid={`delete-compliance-${c.id}`} title="Delete">
                                  <Trash size={12} />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete compliance item?</AlertDialogTitle>
                                  <AlertDialogDescription>{c.title} will be permanently removed.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => remove(c.id)}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}
